import { Injectable } from '@angular/core';
import { filterNil, switchTap } from '@lycoris-nubila/ngx-lycoris';
import { uniqBy } from 'lodash-es';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';

import { SaveUserMoodCmd } from 'user/command/mood/save-user-mood.cmd';
import { UserMoodOutput } from 'user/model/user-mood.output';
import { IUserMood } from 'user/mood/user-mood';
import { IUserNote } from 'user/note/user-note';
import { UserMoodApplicationPort } from 'user/port/mood/user-mood-application.port';
import { UserMoodRepositoryPort } from 'user/port/mood/user-mood-repository.port';
import { UserApiPort } from 'user/port/user-api.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserSessionPort } from 'user/port/user-session.port';
import { UserMoodConverterService } from 'user/user-mood-converter.service';

import { AppLocator } from '../../app.locator';

@Injectable()
export class UserMoodApplicationAdapter implements UserMoodApplicationPort {
	constructor(
		private _userApiPort: UserApiPort,
		private _userSessionPort: UserSessionPort,
		private _userMoodRepositoryPort: UserMoodRepositoryPort,
		private _userMoodConverterService: UserMoodConverterService
	) {}

	private get _userApplicationPort(): UserApplicationPort {
		return AppLocator.inject<UserApplicationPort>(UserApplicationPort);
	}

	public observeLatestUserMood(): Observable<IUserMood> {
		return this._userMoodRepositoryPort.observeLatestUserMood();
	}

	public mustAskMood(): Observable<boolean> {
		return this._userApiPort.mustAskMood();
	}

	public markMoodAsked(): Observable<void> {
		return this._userApiPort.markMoodAsked();
	}

	public fetchLastMood(): Observable<string | Error> {
		return this._userApiPort.fetchLastMood().pipe(
			tap((userMood) => this.persistUserMoods([userMood])),
			map((userMood) => userMood.id),
			catchError((e) => of(Error(e)))
		);
	}

	public fetchOwnedMoods(): Observable<string[] | Error> {
		return this._userApiPort
			.fetchOwnedMoods()
			.pipe()
			.pipe(
				tap((moods) => this.persistUserMoods(moods)),
				map((moods) => moods.map((mood) => mood.id)),
				catchError((e) => of(Error(e)))
			);
	}

	public observeMoodsByIds(ids: string[]): Observable<IUserMood[]> {
		return this._userMoodRepositoryPort.observeUserMoodsByIds(ids);
	}

	public saveUserMood(cmd: SaveUserMoodCmd): Observable<string | Error> {
		return this._userApiPort.saveUserMood(cmd).pipe(
			switchTap(() => this.fetchLastMood()),
			switchTap(() =>
				(cmd.userId
					? of(cmd.userId)
					: this._userApplicationPort
							.observeSessionUserId()
							.pipe(filterNil(), first())
				).pipe(
					tap((userId) =>
						this._userApplicationPort.updateUser({
							moodFrequency: cmd.frequency,
							id: userId,
						})
					)
				)
			),
			catchError((e) => of(Error(e)))
		);
	}

	public persistUserMoods(userMoods: UserMoodOutput[]): void {
		this._userMoodRepositoryPort.persistUserMoods(
			this._userMoodConverterService.userMoodsAsEntities(uniqBy(userMoods, 'id'))
		);
	}
}
